import { useEffect, useMemo } from 'react';
import { FaCalendarDay, FaCreditCard, FaDivide } from 'react-icons/fa';
import { FaPix } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';

import useSWR from 'swr';

import { Header, Loading, Main } from 'components';
import { integerToCurrency } from 'helpers';
import { returnRequestService } from 'services';

import { SearchSection } from '../components/search-section';
import { ConsignmentItem } from './components/consignment-item';
import { ReturnItem } from './components/return-item';

interface InfoFieldProps {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

interface PaymentMethodInfo {
  label: string;
  icon: React.ReactNode;
}

const DEFAULT_PAYMENT_METHOD: PaymentMethodInfo = {
  label: '',
  icon: null,
};

const PAYMENT_METHOD_MAP: Record<string, PaymentMethodInfo> = {
  Pix: { label: 'Pix', icon: <FaPix /> },
  CreditCard: { label: 'Cartão de Crédito', icon: <FaCreditCard /> },
};

const InfoField = ({ label, value, icon }: InfoFieldProps) => (
  <div className="flex flex-col gap-1">
    <p className="text-font14 font-bold text-neutral-500">{label}</p>
    <div className="flex items-center gap-1">
      {icon}
      <p className="text-font14 text-neutral-500">{value}</p>
    </div>
  </div>
);

const formatDate = (date?: string | number) =>
  date ? new Date(date).toLocaleDateString('pt-BR') : '-';

export function ReturnsAndRefundsDetails() {
  const { searchKey } = useParams<{ searchKey: string }>();
  const navigate = useNavigate();

  const params = useMemo(() => {
    if (searchKey?.startsWith('ch_')) {
      return { chargeId: searchKey };
    }

    if (
      searchKey?.toUpperCase().startsWith('RR') ||
      searchKey?.match(/^\d+$/)
    ) {
      return { protocol: searchKey };
    }

    return { orderCode: searchKey || '' };
  }, [searchKey]);

  const { data, isLoading } = useSWR(
    `consignment-and-return-${searchKey}`,
    () => returnRequestService.getConsignmentAndReturn(params)
  );

  const {
    consignments = [],
    createdDate = 0,
    modifiedDate = 0,
    installments = 0,
    orderCode = '',
    paymentMethod = '',
    returnRequests = [],
    totalPrice = { currencyIso: 'BRL', value: 0 },
  } = data || {};

  const hasData =
    data &&
    (data.orderCode ||
      data.consignments?.length > 0 ||
      data.returnRequests?.length > 0);

  const paymentInfo =
    PAYMENT_METHOD_MAP[paymentMethod] ?? DEFAULT_PAYMENT_METHOD;

  const shouldRedirect = !searchKey || (!isLoading && !hasData);

  useEffect(() => {
    if (shouldRedirect) {
      navigate('/devolucoes-e-estornos');
    }
  }, [shouldRedirect, navigate]);

  function renderReturnRequests() {
    if (returnRequests.length === 0) {
      return (
        <p className="text-left text-font14 font-bold text-neutral-500">
          Não há devoluções para este pedido. Caso seja necessário estornar
          algum valor, você pode estornar direto pela remessa.
        </p>
      );
    }

    return (
      <div className="flex w-full flex-col items-start justify-start gap-8">
        {returnRequests.map(returnRequest => (
          <ReturnItem
            key={returnRequest.protocol}
            returnRequest={returnRequest}
          />
        ))}
      </div>
    );
  }

  function renderContent() {
    if (isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      );
    }

    return (
      <>
        <div
          className="relative flex flex-col items-start justify-start gap-1
            self-stretch"
        >
          <p className="text-font14 font-bold text-neutral-500 md:text-font18">
            Pedido
          </p>
          <p className="text-font24 font-bold text-neutral-500 md:text-font36">
            {orderCode}
          </p>
        </div>

        <div
          className="grid w-full grid-cols-2 items-start justify-start gap-3
            md:flex md:flex-row md:gap-6"
        >
          <InfoField
            label="Valor total"
            value={integerToCurrency(totalPrice.value)}
          />

          <InfoField
            label="Pagamento"
            value={paymentInfo.label}
            icon={paymentInfo.icon}
          />

          {installments > 0 && (
            <InfoField
              label="Parcelas"
              value={installments.toString()}
              icon={<FaDivide />}
            />
          )}

          <InfoField
            label="Data de compra"
            value={formatDate(createdDate)}
            icon={<FaCalendarDay />}
          />

          <InfoField
            label="Ultima alteração"
            value={formatDate(modifiedDate)}
            icon={<FaCalendarDay />}
          />
        </div>

        <div
          className="flex flex-col items-start justify-start gap-8 self-stretch
            lg:flex-row"
        >
          <div
            className="relative flex w-full flex-grow flex-col items-start
              justify-start gap-6"
          >
            <p className="text-center text-font24 font-bold text-neutral-700">
              Remessas
            </p>
            {consignments.map(consignment => (
              <ConsignmentItem
                key={consignment.code}
                consignment={consignment}
              />
            ))}
          </div>
          <div
            className="flex w-full flex-col items-start justify-start gap-2
              lg:w-72 lg:gap-6"
          >
            <div
              className="relative flex flex-col items-start justify-start gap-1
                self-stretch"
            >
              <p className="text-center text-font24 font-bold text-neutral-700">
                Devoluções
              </p>
            </div>

            {renderReturnRequests()}
          </div>
        </div>
      </>
    );
  }

  return (
    <Main>
      <Header />

      <div
        className="flex h-full flex-col gap-6 overflow-x-auto overflow-y-auto
          px-4 pb-2 pt-8 md:px-12"
      >
        <SearchSection />

        {renderContent()}
      </div>
    </Main>
  );
}
